import React from 'react';
import PropTypes from 'prop-types';
import { Card as CardBase, getTokensPropType, paddingProp, responsiveProps, selectSystemProps, StackView, useThemeTokens, useThemeTokensCallback, variantProp, a11yProps, viewProps, PressableCardBase, useResponsiveProp } from '@telus-uds/components-base';
import styled from 'styled-components';
import CardContent from './CardContent';
import CardFooter from './CardFooter';
import FullBleedContent, { getFullBleedBorderRadius, useFullBleedContentProps } from '../shared/FullBleedContent';
import ConditionalWrapper from '../shared/ConditionalWrapper';

// Passes React Native-oriented system props through UDS Card
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);

/**
 * A basic card component, unstyled by default.
 *
 * ## Component API
 *
 * ### With Footer
 *
 * Pass any component or simply a string in the `footer` prop in order to render
 * a card with a footer (which uses the `alternative` background).
 *
 * ### With Full Bleed Content
 *
 * Use `fullBleedContent` prop to add a full bleed style image, video or other content to the
 * card. This prop accepts an object with the following properties:
 * - `alt`: alt tag for an image,
 * - `src`: default image source,
 * - `position`: `none`, `bottom`, `left`, `right` or `top`, depending on where you would like your full bleed image to be placed,
 * - `imgCol`: set the span of the image per viewport (based on 12 column value for width of card) when `position` of image is on `left` or `right`
 * - all the props from the `ResponsiveImage` component in case you want that full bleed image to be responsive,
 * - `content`: pass a custom JSX to be used for rendering of the full bleed content (defaults to `ResponsiveImage`
 *   receiving the other props).
 *
 * Note that `position` can be responsive, i.e. different for different viewports. A full bleed content with position
 * {xs: 'none', md: 'left'} for example, will have a full bleed content to the left of card content when viewed on desktops
 * viewports, and no content when viewed on mobile viewports.
 *
 * ## Accessibility
 * `Card` component accepts all the standard accessibility props.
 */
const DynamicWidthContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "Card__DynamicWidthContainer",
  componentId: "components-web__sc-1elbtwd-0"
})(_ref => {
  let {
    width,
    display,
    borderRadius,
    overflow
  } = _ref;
  return {
    width,
    display,
    borderRadius,
    overflow
  };
});
const Card = /*#__PURE__*/React.forwardRef(function () {
  let {
    children,
    footer,
    footerPadding,
    fullBleedImage,
    fullBleedContent = fullBleedImage,
    tokens = {},
    variant,
    interactiveCard,
    onPress,
    dataSet,
    backgroundImage,
    ...rest
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
    fullBleedContent: {
      position: null
    }
  };
  let ref = arguments.length > 1 ? arguments[1] : undefined;
  const {
    contentStackAlign,
    contentStackDirection,
    fullBleedContentPosition,
    fullBleedContentProps
  } = useFullBleedContentProps(fullBleedContent);
  const {
    imgCol
  } = fullBleedContentProps;

  // If the card has rounded corners and a full bleed image, we need to apply
  // those corners on the image as well, but partially
  const {
    borderRadius
  } = useThemeTokens('Card', tokens, variant);
  const getThemeTokens = useThemeTokensCallback('Card', interactiveCard?.tokens, {
    interactive: true,
    ...(interactiveCard?.variant || {})
  });
  const hasFooter = Boolean(footer);
  const fullBleedBorderRadius = getFullBleedBorderRadius(borderRadius, fullBleedContentPosition, hasFooter);

  // takes imgCol from fullBleedContent if present, to dynamically set width of image
  // card content will adapt to the size of image to add up to 100% width of card width
  // pass as props to ConditionalWrapper
  const imgColCurrentViewport = useResponsiveProp(imgCol);
  const maxCol = 12;
  const fullBleedImageWidth = `${imgColCurrentViewport / maxCol * 100}%`;
  const adaptiveContentWidth = `${(maxCol - imgColCurrentViewport) / maxCol * 100}%`;
  const isImageWidthAdjustable = imgCol && (fullBleedContentPosition === 'left' || fullBleedContentPosition === 'right');
  const contentWrapperStyleProps = {
    width: adaptiveContentWidth,
    display: imgColCurrentViewport >= maxCol ? 'none' : undefined
  };
  const imageWrapperStyleProps = {
    width: fullBleedImageWidth,
    borderRadius: imgColCurrentViewport >= maxCol ? borderRadius : undefined,
    overflow: imgColCurrentViewport >= maxCol ? 'hidden' : undefined,
    display: imgColCurrentViewport === 0 ? 'none' : undefined
  };
  const columnFlex = {
    flexGrow: interactiveCard?.body ? 0 : 1,
    flexShrink: 1,
    justifyContent: 'space-between'
  };
  // When interactive area is present, spacing tokens should only be applied
  // to individual Card sections, not Card as a whole
  const {
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    ...restOfTokens
  } = tokens;
  return /*#__PURE__*/_jsxs(CardBase, {
    ref: ref,
    variant: {
      ...variant,
      padding: 'custom'
    },
    tokens: restOfTokens,
    backgroundImage: backgroundImage,
    ...selectProps(rest),
    children: [interactiveCard?.body ? /*#__PURE__*/_jsx(PressableCardBase, {
      ref: ref,
      tokens: getThemeTokens,
      dataSet: dataSet,
      onPress: onPress,
      ...selectProps(rest),
      children: typeof interactiveCard?.body === 'function' ? interactiveCard.body() : interactiveCard.body
    }) : null, children || fullBleedContentPosition !== 'none' ? /*#__PURE__*/_jsxs(StackView, {
      direction: contentStackDirection,
      tokens: {
        ...columnFlex,
        alignItems: contentStackAlign
      },
      space: 0,
      children: [children ? /*#__PURE__*/_jsx(ConditionalWrapper, {
        WrapperComponent: DynamicWidthContainer,
        wrapperProps: contentWrapperStyleProps,
        condition: isImageWidthAdjustable,
        children: /*#__PURE__*/_jsx(CardContent, {
          tokens: tokens,
          variant: variant,
          withFooter: hasFooter,
          children: children
        })
      }) : null, fullBleedContentPosition !== 'none' && /*#__PURE__*/_jsx(ConditionalWrapper, {
        WrapperComponent: DynamicWidthContainer,
        wrapperProps: imageWrapperStyleProps,
        condition: isImageWidthAdjustable,
        children: /*#__PURE__*/_jsx(FullBleedContent, {
          borderRadius: fullBleedBorderRadius,
          ...fullBleedContentProps,
          position: fullBleedContentPosition
        })
      })]
    }) : null, footer && /*#__PURE__*/_jsx(CardFooter, {
      padding: footerPadding,
      tokens: tokens,
      variant: variant,
      children: footer
    })]
  });
});
const positionValues = ['none', 'bottom', 'left', 'right', 'top'];
const alignValues = ['start', 'end', 'center', 'stretch'];
const PositionedFullBleedContentPropType = PropTypes.shape({
  position: responsiveProps.getTypeOptionallyByViewport(PropTypes.oneOf(positionValues)).isRequired,
  align: responsiveProps.getTypeOptionallyByViewport(PropTypes.oneOf(alignValues)),
  // eslint-disable-next-line react/forbid-foreign-prop-types
  ...FullBleedContent.propTypes
});
Card.displayName = 'Card';
Card.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Card content.
   */
  children: PropTypes.node,
  /**
   * Card footer.
   */
  footer: PropTypes.node,
  /**
   * Custom card footer padding.
   */
  footerPadding: paddingProp.propType,
  /**
   * Full bleed image to be placed on the card, deprecated in favor of `fullBleedContent`.
   *
   * @deprecated
   */
  fullBleedImage: PositionedFullBleedContentPropType,
  /**
   * Full bleed content to be placed on the card.
   */
  fullBleedContent: PositionedFullBleedContentPropType,
  /**
   * Card tokens.
   */
  tokens: getTokensPropType('Card'),
  /**
   * Card variant.
   */
  variant: variantProp.propType,
  /**
   * Function to call on pressing the card.
   * Note: This is only available when `interactive` variant is used.
   */
  onPress: PropTypes.func,
  /**
   * Object to set interactive card's properties
   */
  interactiveCard: PropTypes.shape({
    body: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    tokens: getTokensPropType('Card'),
    variant: variantProp.propType
  }),
  /**
   * Apply background image to the card.
   */
  backgroundImage: PropTypes.shape({
    // src is either a URI string or an object when used responsively to provide different image sources for different screen sizes
    src: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    alt: PropTypes.string,
    resizeMode: responsiveProps.getTypeOptionallyByViewport(PropTypes.oneOf(['cover', 'contain', 'stretch', 'repeat', 'center']))
  })
};
export default Card;